
import { useState } from 'react';
import { Navbar } from '../home/components/Navbar';
import { Footer } from '../home/components/Footer';
import { useNavigate } from 'react-router-dom';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQCategory {
  title: string;
  icon: string;
  items: FAQItem[];
}

const faqCategories: FAQCategory[] = [
  {
    title: 'Getting Started',
    icon: 'ri-rocket-line',
    items: [
      {
        question:
          'What is a short-term rental (STR) and how is it different from a long-term rental?',
        answer:
          'A short-term rental is typically a furnished property rented for less than 30 consecutive days, often listed on platforms like Airbnb or VRBO. Long-term rentals are unfurnished leases of 12 months or more. Medium-term rentals fall in between, usually 30 to 90 days, and are popular with relocating professionals and insurance claimants.',
      },
      {
        question: 'How do I know if my property is eligible for short-term rentals?',
        answer:
          "Eligibility depends on your city's bylaws, your property’s zoning designation, and any strata or HOA restrictions. In many Canadian cities, you must live in the property as your principal residence to operate an STR. Our Compliance & Eligibility Review can give you a clear answer within 3-5 business days.",
      },
      {
        question:
          'Do I need a business license to operate a short-term rental in Canada?',
        answer:
          'Yes, most Canadian cities require a specific short-term rental license or business license. In British Columbia, you also need a Provincial Registry Number. Requirements and fees vary by municipality, so it\'s important to check your local regulations before listing.',
      },
      {
        question: 'What is the BC Provincial STR Registry?',
        answer:
          'As of May 2024, British Columbia requires all short-term rental operators to register with the provincial registry and display their registry number on all listings. This applies to all BC municipalities and is enforced through platform compliance — Airbnb and VRBO will remove listings without valid registry numbers.',
      },
      {
        question: 'Can I rent out my condo as a short-term rental?',
        answer:
          'It depends on your strata bylaws. Many strata corporations in BC and Ontario have bylaws that restrict or prohibit short-term rentals. Even if your city allows STRs, your strata can override that permission. We review strata bylaws as part of our compliance service.',
      },
    ],
  },
  {
    title: 'Regulations & Compliance',
    icon: 'ri-shield-check-line',
    items: [
      {
        question: 'What is the principal residence requirement?',
        answer:
          'Many cities, including Vancouver, Toronto, and Victoria, require that you live in the property as your primary home to operate a short-term rental. This means you cannot STR an investment property in these cities. Calgary and Edmonton do not have this requirement, making them more investor‑friendly.',
      },
      {
        question: 'What happens if I operate without a license?',
        answer:
          'Penalties vary by city but can be severe. Vancouver fines start at $1,000 per day of violation. Toronto can issue fines up to $10,000 per offence. Platforms like Airbnb are also required to remove non‑compliant listings, so operating without a license puts both your income and your listing at risk.',
      },
      {
        question: 'Are there limits on how many nights I can rent per year?',
        answer:
          'Yes, some cities impose annual caps. Toronto limits entire‑home STRs to 180 nights per year. Vancouver allows 365 nights but only for your principal residence. Calgary and Edmonton have no night limits. These caps only apply to entire‑home rentals — private room rentals are often exempt.',
      },
      {
        question: 'What taxes do I need to pay on short-term rental income?',
        answer:
          "You'll need to report all rental income on your tax return. In BC, you must collect and remit the Municipal and Regional District Tax (MRDT) of 8%. In Toronto, there's a 6% Municipal Accommodation Tax (MAT). If your annual revenue exceeds $30,000, you must also register for and collect GST/HST.",
      },
      {
        question: 'Do I need insurance for my short-term rental?',
        answer:
          "Standard homeowner's insurance typically does not cover short-term rental activity. You should notify your insurer and obtain a commercial or STR‑specific policy. Airbnb's Host Protection Insurance provides some coverage, but it should not be your only protection.",
      },
    ],
  },
  {
    title: 'Our Services',
    icon: 'ri-service-line',
    items: [
      {
        question: 'What does your Compliance & Eligibility Review include?',
        answer:
          "Our review covers city bylaw analysis, zoning verification, strata bylaw review (for condos), a detailed compliance checklist, risk assessment, and a 30‑minute expert consultation. You'll receive a clear report outlining what's allowed, what's not, and your next steps.",
      },
      {
        question: 'What is co-hosting and how does it work?',
        answer:
          'Co‑hosting means we manage the day‑to‑day operations of your short‑term rental while you remain the licensed host. We handle guest communication, pricing optimization, cleaning coordination, and listing management. You keep ownership and control — we handle the work.',
      },
      {
        question: 'What are medium-term furnished rentals?',
        answer:
          'Medium‑term rentals are stays of 30 to 90+ days in fully furnished properties. They\'re popular with corporate relocations, insurance housing, and remote workers. In many cities where STRs are restricted, medium‑term rentals offer a compliant alternative with stable, predictable income.',
      },
      {
        question: 'How much does your co-hosting service cost?',
        answer:
          'Our co‑hosting fees are performance‑based, typically a percentage of your rental revenue. This means we only earn when you earn. Exact rates depend on your property type, location, and service level. Contact us for a personalized quote.',
      },
      {
        question: 'Do you help with listing setup and photography?',
        answer:
          'Yes. Our Listing & Setup Support service includes professional photography coordination, listing copywriting optimized for search, pricing strategy, and platform setup across Airbnb, VRBO, and other channels. We make sure your property launches with maximum visibility.',
      },
    ],
  },
  {
    title: 'Revenue & Pricing',
    icon: 'ri-money-dollar-circle-line',
    items: [
      {
        question: 'How much can I earn from a short-term rental in Canada?',
        answer:
          'Earnings vary significantly by city, property type, and season. A well‑managed one‑bedroom in downtown Vancouver can earn $3,000‑$5,000/month. Calgary properties often see $2,500‑$4,000/month. Use our Revenue Calculator for a personalized estimate based on your specific property details.',
      },
      {
        question: 'Is short-term rental income better than long-term rental income?',
        answer:
          'In many cases, yes. Short‑term rentals can generate 2‑3× the income of a traditional long‑term lease, especially in high‑demand areas. However, STRs come with higher operating costs, more management effort, and regulatory requirements. Medium‑term rentals offer a middle ground with good income and lower hassle.',
      },
      {
        question: 'What factors affect my rental revenue the most?',
        answer:
          'The biggest factors are location, property quality, pricing strategy, and occupancy rate. Professional photography, optimized listings, dynamic pricing, and excellent guest reviews can increase your revenue by 20‑40% compared to a self‑managed listing.',
      },
      {
        question: 'When is peak season for short-term rentals in Canada?',
        answer:
          'Peak season is generally June through September across most Canadian cities. Whistler also sees strong winter demand (December‑March). In 2026, expect additional demand spikes around FIFA World Cup events in Vancouver and Toronto. Calgary Stampede (July) is another major driver.',
      },
    ],
  },
  {
    title: 'Buildings & Properties',
    icon: 'ri-building-2-line',
    items: [
      {
        question: 'How do I know if my building allows short-term rentals?',
        answer:
          'Check your strata bylaws or HOA rules first. Many buildings have explicit restrictions on rentals under 30 days. Our STR Buildings tool helps you search for buildings in Metro Vancouver that are known to permit short‑term rentals. You can also request a compliance review for a definitive answer.',
      },
      {
        question: "Can I STR a property I don't live in?",
        answer:
          'In cities with principal residence requirements (Vancouver, Toronto, Victoria), no — you must live there. In Calgary, Edmonton, and Whistler (in approved zones), you can operate STRs on investment properties. Always verify your specific city\'s rules before proceeding.',
      },
      {
        question: 'What if my strata says no but the city says yes?',
        answer:
          'Your strata bylaws take precedence for your specific building. Even if the city permits STRs in your zone, your strata corporation can prohibit them. This is one of the most common issues we encounter, which is why we always review strata bylaws as part of our compliance assessment.',
      },
    ],
  },
];

function AccordionItem({
  item,
  isOpen,
  onToggle,
}: {
  item: FAQItem;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border border-[#E8E4E0] rounded-xl overflow-hidden transition-all hover:border-[#882629]/30">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer bg-white hover:bg-[#FDFBF9] transition-colors"
      >
        <span className="text-base font-medium text-[#505A5A] pr-4">{item.question}</span>
        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
          <i
            className={`ri-arrow-down-s-line text-xl text-[#882629] transition-transform duration-300 ${
              isOpen ? 'rotate-180' : ''
            }`}
          ></i>
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-[500px]' : 'max-h-0'}`}>
        <div className="px-6 pb-6 pt-0">
          <p className="text-sm text-[#505A5A]/75 leading-relaxed">{item.answer}</p>
        </div>
      </div>
    </div>
  );
}

export default function FAQPage() {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const navigate = useNavigate();

  const toggleItem = (key: string) => {
    setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const filteredCategories =
    activeCategory === 'All'
      ? faqCategories
      : faqCategories.filter((cat) => cat.title === activeCategory);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#F9F5F0] to-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <div className="text-xs font-semibold text-[#882629] uppercase tracking-widest mb-5">
            Knowledge Base
          </div>
          <h1 className="font-['Fraunces'] text-5xl lg:text-6xl font-bold text-[#505A5A] mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-[#505A5A]/70 leading-relaxed max-w-2xl mx-auto">
            Everything you need to know about short-term rental regulations, compliance, and our services
            across Canada.
          </p>
        </div>
      </section>

      {/* Category Pills */}
      <section className="py-8 bg-white border-b border-[#F0F0F0] sticky top-24 z-40">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div
            className="flex items-center gap-3 overflow-x-auto pb-1 scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <button
              onClick={() => setActiveCategory('All')}
              className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                activeCategory === 'All'
                  ? 'bg-[#882629] text-white'
                  : 'bg-[#F0F0F0] text-[#505A5A] hover:bg-[#E8E4E0]'
              }`}
            >
              All
            </button>
            {faqCategories.map((cat) => (
              <button
                key={cat.title}
                onClick={() => setActiveCategory(cat.title)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                  activeCategory === cat.title
                    ? 'bg-[#882629] text-white'
                    : 'bg-[#F0F0F0] text-[#505A5A] hover:bg-[#E8E4E0]'
                }`}
              >
                {cat.title}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          {filteredCategories.map((category) => (
            <div key={category.title} className="mb-14 last:mb-0">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-[#882629]/10 rounded-lg">
                  <i className={`${category.icon} text-xl text-[#882629]`}></i>
                </div>
                <h2 className="font-['Fraunces'] text-2xl font-bold text-[#505A5A]">
                  {category.title}
                </h2>
              </div>
              <div className="space-y-3">
                {category.items.map((item, idx) => {
                  const key = `${category.title}-${idx}`;
                  return (
                    <AccordionItem
                      key={key}
                      item={item}
                      isOpen={!!openItems[key]}
                      onToggle={() => toggleItem(key)}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="py-20 bg-[#F9F5F0]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <div className="bg-white rounded-3xl p-12 shadow-sm">
            <div className="w-16 h-16 flex items-center justify-center bg-[#882629]/10 rounded-full mx-auto mb-6">
              <i className="ri-question-answer-line text-3xl text-[#882629]"></i>
            </div>
            <h2 className="font-['Fraunces'] text-3xl font-bold text-[#505A5A] mb-4">
              Still have questions?
            </h2>
            <p className="text-base text-[#505A5A]/70 mb-8 leading-relaxed">
              Our team is here to help. Get a personalized answer about your property and situation.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => navigate('/property-assessment')}
                className="bg-[#882629] text-white px-8 py-3.5 rounded-full text-sm font-semibold hover:bg-[#6E1F22] transition-all cursor-pointer whitespace-nowrap shadow-md"
              >
                Check my property eligibility
              </button>
              <button
                onClick={() => navigate('/book-call')}
                className="border-2 border-[#882629] text-[#882629] px-8 py-3.5 rounded-full text-sm font-semibold hover:bg-[#882629] hover:text-white transition-all cursor-pointer whitespace-nowrap"
              >
                Book a free intro call
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
